import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import GroupsIcon from "@mui/icons-material/Groups";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const ManagerOvertimeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [remarks, setRemarks] = useState({});
  const [actingOnId, setActingOnId] = useState(null);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/overtime/manager-requests`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch overtime requests");
      }

      setRequests(data.overtimeRequests || []);
    } catch (err) {
      setError(err.message || "Failed to fetch overtime requests");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((r) => r.status === "pending").length;
    const approved = requests.filter((r) => r.status === "approved").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    return { total, pending, approved, rejected };
  }, [requests]);

  useEffect(() => {
    fetchRequests();
  }, []);

  const filteredRequests = useMemo(
    () =>
      requests.filter((request) => {
        const matchesSearch =
          request.employee?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          request.employee?.employeeid
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesStatus =
          filterStatus === "all" || request.status === filterStatus;

        return matchesSearch && matchesStatus;
      }),
    [requests, searchTerm, filterStatus]
  );

  const handleStatusUpdate = async (id, status) => {
    try {
      setError("");
      setActingOnId(id);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/overtime/update-status/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status,
          managerRemark: remarks[id] || "",
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to update overtime status");
      }

      setRequests((prev) =>
        prev.map((request) =>
          request._id === id
            ? {
                ...request,
                status,
                managerRemark: remarks[id] || "",
              }
            : request
        )
      );
    } catch (err) {
      setError(err.message || "Failed to update overtime status");
    } finally {
      setActingOnId(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Overtime Approvals"
        subtitle="Review, approve, or reject overtime requests submitted by your team"
        icon={<MoreTimeIcon />}
        actions={
          <IconButton
            onClick={fetchRequests}
            disabled={loading}
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.24)",
              border: "1px solid rgba(255,255,255,0.55)",
              borderRadius: "10px",
              p: 1,
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              color: "#ffffff",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.34)",
                borderColor: "rgba(255,255,255,0.8)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.24)",
              },
              "&:focus-visible": {
                outline: "2px solid #ffffff",
                outlineOffset: "2px",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(255,255,255,0.16)",
                borderColor: "rgba(255,255,255,0.35)",
                color: "rgba(255,255,255,0.6)",
              },
            }}
            aria-label="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
      />

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Total Requests"
            value={stats.total}
            icon={<GroupsIcon />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Pending"
            value={stats.pending}
            icon={<PendingActionsIcon />}
            color="#92400E"
            bgColor="#FEF3C7"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Approved"
            value={stats.approved}
            icon={<CheckCircleIcon />}
            color="#059669"
            bgColor="#D1FAE5"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Rejected"
            value={stats.rejected}
            icon={<CancelIcon />}
            color="#DC2626"
            bgColor="#FEE2E2"
          />
        </Grid>
      </Grid>

      {/* Search / Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #eef0f6",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="stretch">
          <TextField
            placeholder="Search by name or employee ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#8b8fa3" }} />
                </InputAdornment>
              ),
            }}
          />
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 180 }, borderRadius: "8px", fontSize: "0.875rem" }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #eef0f6",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={44} />
              <Skeleton variant="rounded" height={44} />
              <Skeleton variant="rounded" height={44} />
            </Stack>
          </Box>
        ) : filteredRequests.length === 0 ? (
          <EmptyState
            title="No overtime requests"
            subtitle="There are no overtime requests matching your filters."
          />
        ) : (
          <>
            {/* Desktop Table */}
            <TableContainer sx={{ display: { xs: "none", lg: "block" }, overflowX: "auto", maxWidth: "100%" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Employee", "Date", "Time", "Hours", "Reason", "Status", "Remark", "Action"].map(
                      (header) => (
                        <TableCell
                          key={header}
                          sx={{
                            bgcolor: "#f5f7fb",
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            color: "#5a5f7a",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            py: 1.5,
                            borderBottom: "2px solid #f0f0f5",
                          }}
                        >
                          {header}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow
                      key={request._id}
                      sx={{ "&:hover": { bgcolor: "#f5f7fb" } }}
                    >
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: "#eef0ff",
                              color: "#667eea",
                              width: 34,
                              height: 34,
                              fontSize: "0.875rem",
                              fontWeight: 600,
                            }}
                          >
                            {request.employee?.name?.charAt(0)?.toUpperCase() || "E"}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                              {request.employee?.name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                              {request.employee?.employeeid}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        {formatDate(request.workDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <AccessTimeIcon sx={{ fontSize: 14, color: "#8b8fa3" }} />
                          <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                            {request.startTime} - {request.endTime}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2, fontWeight: 600 }}>
                        {request.totalHours}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        {request.reason}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <StatusChip status={request.status} />
                      </TableCell>
                      <TableCell sx={{ py: 2, minWidth: 200 }}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add remark"
                          value={remarks[request._id] ?? request.managerRemark ?? ""}
                          onChange={(e) =>
                            setRemarks((prev) => ({
                              ...prev,
                              [request._id]: e.target.value,
                            }))
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8125rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            disabled={actingOnId === request._id}
                            onClick={() => handleStatusUpdate(request._id, "approved")}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "8px",
                              fontSize: "0.8125rem",
                              px: 2,
                              py: 0.5,
                              bgcolor: "#059669",
                              color: "#fff",
                              "&:hover": { bgcolor: "#047857" },
                              "&:disabled": { bgcolor: "#D1FAE5", color: "#065F46" },
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={actingOnId === request._id}
                            onClick={() => handleStatusUpdate(request._id, "rejected")}
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "8px",
                              fontSize: "0.8125rem",
                              px: 2,
                              py: 0.5,
                              borderColor: "#DC2626",
                              color: "#DC2626",
                              "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                            }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile Cards */}
            <Box sx={{ display: { xs: "block", lg: "none" }, p: 2 }}>
              <Grid container spacing={2}>
                {filteredRequests.map((request) => (
                  <Grid item xs={12} key={request._id}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "16px",
                        border: "1px solid #eef0f6",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ px: 2.5, py: 2, bgcolor: "#f5f7fb", borderBottom: "2px solid #f0f0f5" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: "#eef0ff",
                                color: "#667eea",
                                width: 34,
                                height: 34,
                                fontSize: "0.875rem",
                                fontWeight: 600,
                              }}
                            >
                              {request.employee?.name?.charAt(0)?.toUpperCase() || "E"}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {request.employee?.name}
                              </Typography>
                              <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                                {request.employee?.employeeid}
                              </Typography>
                            </Box>
                          </Box>
                          <StatusChip status={request.status} />
                        </Stack>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        <Stack spacing={1.5}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Date</Typography>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1a1a2e" }}>
                              {formatDate(request.workDate)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Time</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <AccessTimeIcon sx={{ fontSize: 14, color: "#8b8fa3" }} />
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {request.startTime} - {request.endTime}
                              </Typography>
                            </Stack>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Total Hours</Typography>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#1a1a2e" }}>
                              {request.totalHours}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.5 }}>Reason</Typography>
                            <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e", wordBreak: "break-word" }}>
                              {request.reason}
                            </Typography>
                          </Box>

                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Add remark"
                            value={remarks[request._id] ?? request.managerRemark ?? ""}
                            onChange={(e) =>
                              setRemarks((prev) => ({
                                ...prev,
                                [request._id]: e.target.value,
                              }))
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8125rem" },
                            }}
                          />

                          <Stack direction="row" spacing={1}>
                            <Button
                              fullWidth
                              size="small"
                              disabled={actingOnId === request._id}
                              onClick={() => handleStatusUpdate(request._id, "approved")}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "8px",
                                py: 1,
                                bgcolor: "#059669",
                                color: "#fff",
                                "&:hover": { bgcolor: "#047857" },
                              }}
                            >
                              Approve
                            </Button>
                            <Button
                              fullWidth
                              size="small"
                              variant="outlined"
                              disabled={actingOnId === request._id}
                              onClick={() => handleStatusUpdate(request._id, "rejected")}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "8px",
                                py: 1,
                                borderColor: "#DC2626",
                                color: "#DC2626",
                                "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                              }}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}
      </Paper>
    </PageContainer>
  );
};

export default ManagerOvertimeRequests;
